import { calcFutureValue } from '../utils/calcFutureValue';
import { isNullOrUndefined } from '../../isNullOrUndefined';
import { analyzeInvestmentReturns } from '../roi/calcProjectedROI';
import { Draft } from 'immer';
import {
  ReturnOnInvestmentCalculations,
  calculateMonthsBetweenYears,
} from './fundingTypes';

function calcDrawdownImpact(
  selfFunding: SelfFundingSource,
  ltcAtYear: number,
): number {
  if (!selfFunding.hasDrawdown || !selfFunding.drawdownAmount) {
    return 0;
  }

  const drawdownStartYear = selfFunding.drawdownStartAtYear ?? new Date().getFullYear();
  const drawdownEndYear = selfFunding.drawdownEndAtYear ?? ltcAtYear;
  const drawdownYears = Math.max(0, drawdownEndYear - drawdownStartYear);

  if (drawdownYears === 0) return 0;

  const monthlyDrawdown =
    selfFunding.drawdownAmountInterval === 'yearly'
      ? selfFunding.drawdownAmount / 12
      : selfFunding.drawdownAmount;

  const annualDrawdown = monthlyDrawdown * 12;
  const totalWithdrawn = annualDrawdown * drawdownYears;

  selfFunding.drawdownStartYear = drawdownStartYear;
  selfFunding.drawdownEndYear = drawdownEndYear;
  selfFunding.drawdownTotalWithdrawn = totalWithdrawn;

  // Account for lost compounding: withdrawals taken early can no longer grow,
  // so their true cost in future value is higher than the nominal sum.
  const annualRateOfReturn = selfFunding.annualRateOfReturn ?? 0;
  const drawdownMonths = calculateMonthsBetweenYears(drawdownStartYear, drawdownEndYear);
  const fvAtDrawdownEnd = calcFutureValueOfMonthlyContributions(
    monthlyDrawdown,
    annualRateOfReturn,
    drawdownMonths,
  );
  const yearsFromDrawdownEndToLtc = Math.max(0, ltcAtYear - drawdownEndYear);

  return calcFutureValue(fvAtDrawdownEnd, annualRateOfReturn, yearsFromDrawdownEndToLtc);
}

function calcNetFutureValueOfExistingAssets(selfFunding: SelfFundingSource, yearsTillLtc: number) {
  const { existingAssetsValue, annualRateOfReturn, gainsTaxRate } = selfFunding;

  selfFunding.futureValueOfInitialInvestment =
    isNullOrUndefined(existingAssetsValue) || isNullOrUndefined(annualRateOfReturn)
      ? 0
      : calcFutureValue(existingAssetsValue, annualRateOfReturn, yearsTillLtc);

  const gainsTaxLiabilityOnExistingAssets: number = calcCapitalGainsTaxLiability(
    existingAssetsValue,
    gainsTaxRate,
    selfFunding.futureValueOfInitialInvestment,
  );
  return selfFunding.futureValueOfInitialInvestment - gainsTaxLiabilityOnExistingAssets;
}

function calcNetFutureValueOfMonthlyContributions(selfFunding: SelfFundingSource) {
  const { monthlyContributionAmount, annualRateOfReturn, gainsTaxRate } = selfFunding;

  const futureValueOfMonthlyContributions: number = isNullOrUndefined(monthlyContributionAmount)
    ? 0
    : calcFutureValueOfMonthlyContributions(
        monthlyContributionAmount,
        annualRateOfReturn ?? 0,
        selfFunding.contributionMonthCount,
      );

  const gainsTaxLiabilityOnfMonthlyContributions: number = calcCapitalGainsTaxLiability(
    (monthlyContributionAmount ?? 0) * selfFunding.contributionMonthCount,
    gainsTaxRate,
    futureValueOfMonthlyContributions,
  );

  return futureValueOfMonthlyContributions - gainsTaxLiabilityOnfMonthlyContributions;
}

export function calcSelfFundingProjectedCostCoverage(mutableClient: Draft<Client>) {
  const {
    fundingSources: {
      selfFunding,
      selfFunding: { contributionStartYear, monthlyIncome },
    },
    allPhaseCosts: { allPhaseCareMonthsNeeded },
    appliedInferenceSet,
  } = mutableClient;

  const ltcAtYear = appliedInferenceSet.ltcAtYear ?? selfFunding.contributionEndYear;
  // Use user-specified stop year if set, capped to not exceed LTC start
  selfFunding.contributionEndYear =
    selfFunding.contributionStopYear != null
      ? Math.min(selfFunding.contributionStopYear, ltcAtYear)
      : ltcAtYear;
  const contributionEndYear = selfFunding.contributionEndYear;

  // calc net future value of initial investment
  selfFunding.contributionYearCount = contributionEndYear - contributionStartYear;
  selfFunding.contributionMonthCount =
    selfFunding.contributionYearCount > 0
      ? calculateMonthsBetweenYears(contributionStartYear, contributionEndYear)
      : 1;

  const netFutureValueOfInitialInvestment = calcNetFutureValueOfExistingAssets(
    selfFunding,
    appliedInferenceSet.yearsTillLtc,
  );
  // calc net future value of monthly contributions
  const netFutureValueOfMonthlyContributions: number =
    calcNetFutureValueOfMonthlyContributions(selfFunding);

  // calc total value of monthly income (gross — used for display and cost basis)
  selfFunding.monthlyIncomeTotal = allPhaseCareMonthsNeeded * (monthlyIncome ?? 0);
  const netMonthlyIncome =
    selfFunding.monthlyIncomeTotal * (1 - (selfFunding.monthlyIncomeTaxRate ?? 0));

  // combine all values to get total value (use net income for after-tax projection)
  selfFunding.selfFundingTotalValue =
    netFutureValueOfInitialInvestment +
    netFutureValueOfMonthlyContributions +
    netMonthlyIncome;

  // Subtract drawdown withdrawals (drawdown runs until LTC starts, not until contributions stop)
  const drawdownTotal = calcDrawdownImpact(selfFunding, ltcAtYear);
  selfFunding.selfFundingTotalValue = Math.max(0, selfFunding.selfFundingTotalValue - drawdownTotal);

  const totalValue = selfFunding.selfFundingTotalValue;
  // Assume all funds can be used for LTC
  selfFunding.selfFundingProjectedCostCoverage = totalValue;
}

export function calcCapitalGainsTaxLiability(
  principal: number | null,
  gainsTaxRate: number | null,
  investmentFutureValue: number,
) {
  if (isNullOrUndefined(principal) || isNullOrUndefined(gainsTaxRate)) {
    return 0;
  }
  return (investmentFutureValue - principal) * gainsTaxRate;
}

export function calcFutureValueOfMonthlyContributions(
  monthlyContributionAmount: number,
  annualRateOfReturn: number,
  contributionMonthCount: number,
): number {
  if (
    isNullOrUndefined(monthlyContributionAmount) ||
    isNullOrUndefined(annualRateOfReturn) ||
    isNullOrUndefined(contributionMonthCount)
  ) {
    return 0;
  }
  if (annualRateOfReturn === 0) {
    return monthlyContributionAmount * contributionMonthCount;
  }

  return (
    monthlyContributionAmount *
    ((Math.pow(1 + annualRateOfReturn / 12, contributionMonthCount) - 1) /
      (annualRateOfReturn / 12))
  );
}

/**
 * Dedicated calculation for retirement-income sources.
 * Retirement income is an income stream that starts at LTC start age and pays
 * over the care period — it is NOT an investment that compounds.
 *
 * Formula: (annualIncome / 12) × caremonths × (1 − incomeTaxRate)
 *
 * `existingAssetsValue` stores the annual income amount.
 * `gainsTaxRate` is reinterpreted as an income tax rate for this source type.
 */
export function calcRetirementIncomeProjectedCoverage(mutableClient: Draft<Client>) {
  const {
    fundingSources: {
      selfFunding,
      selfFunding: { existingAssetsValue, gainsTaxRate },
    },
    allPhaseCosts: { allPhaseCareMonthsNeeded },
  } = mutableClient;

  const annualIncome = existingAssetsValue ?? 0;
  const incomeTaxRate = gainsTaxRate ?? 0;
  const monthlyIncome = annualIncome / 12;
  const grossTotal = monthlyIncome * allPhaseCareMonthsNeeded;
  const netTotal = grossTotal * (1 - incomeTaxRate);

  selfFunding.monthlyIncome = monthlyIncome;
  selfFunding.monthlyIncomeTotal = grossTotal;
  selfFunding.selfFundingTotalValue = netTotal;
  selfFunding.selfFundingProjectedCostCoverage = netTotal;
  selfFunding.selfFundingTotalCost = annualIncome;
  selfFunding.selfFundingNonLtcPayout = 0;

  // No investment growth fields apply to income streams
  selfFunding.futureValueOfInitialInvestment = 0;
  selfFunding.contributionYearCount = 0;
  selfFunding.contributionMonthCount = 0;
  selfFunding.monthlyContributionAmount = null;
  selfFunding.annualRateOfReturn = 0;

  // ROI metrics are not meaningful for income streams
  selfFunding.selfFundingProjectedReturnOnInvestment = 0;
  selfFunding.selfFundingProjectedCompoundAnnualGrowthRate = 0;
  selfFunding.selfFundingProjectedInternalRateOfReturn = 0;
  selfFunding.investmentPeriodYears = 0;
  selfFunding.investmentCashFlows = [];
  selfFunding.returnCashFlows = [];
}

export function calcSelfFundingTotalsOnClaim(mutableClient: Draft<Client>) {
  const {
    fundingSources: {
      selfFunding,
      selfFunding: { monthlyIncome, existingAssetsValue },
    },
    allPhaseCosts: { allPhaseCareMonthsNeeded },
  } = mutableClient;

  selfFunding.monthlyIncomeTotal = allPhaseCareMonthsNeeded * (monthlyIncome ?? 0);
  const netMonthlyIncome =
    selfFunding.monthlyIncomeTotal * (1 - (selfFunding.monthlyIncomeTaxRate ?? 0));

  // total value is equal to sum of existing assets and net monthly income
  selfFunding.selfFundingTotalValue = (existingAssetsValue ?? 0) + netMonthlyIncome;

  // set total cost and coverage equal to toal value
  selfFunding.selfFundingTotalCost = selfFunding.selfFundingTotalValue;
  selfFunding.selfFundingProjectedCostCoverage = selfFunding.selfFundingTotalValue;

  // assume that all funds can be used for LTC, so this is zero.
  selfFunding.selfFundingNonLtcPayout = 0;
}

export function calcSelfFundingTotalCost(mutableClient: Draft<Client>) {
  const {
    fundingSources: {
      selfFunding,
      selfFunding: {
        existingAssetsValue,
        monthlyContributionAmount,
        monthlyIncomeTotal,
        contributionMonthCount,
      },
    },
  } = mutableClient;
  if (
    isNullOrUndefined(existingAssetsValue) &&
    isNullOrUndefined(monthlyContributionAmount) &&
    isNullOrUndefined(monthlyIncomeTotal)
  ) {
    selfFunding.selfFundingTotalCost = undefined;
    return;
  }

  selfFunding.selfFundingTotalCost =
    (monthlyContributionAmount ?? 0) * contributionMonthCount +
    (existingAssetsValue ?? 0) +
    (monthlyIncomeTotal ?? 0);
}

export function calcSelfFundingProjectedROI(
  mutableClient: Draft<Client>,
  discountRate?: number,
): ReturnOnInvestmentCalculations {
  const {
    fundingSources: {
      selfFunding: { selfFundingTotalValue, selfFundingTotalCost, contributionStartYear },
      selfFunding,
    },
  } = mutableClient;

  if (isNullOrUndefined(selfFundingTotalValue) || !selfFundingTotalCost) {
    return {
      investments: [],
      returns: [],
      investmentPeriodYears: 0,
    };
  }

  // assume that all funds can be used for LTC, so this is zero.
  selfFunding.selfFundingNonLtcPayout = 0;

  const returnOnInvestment = (selfFundingTotalValue - selfFundingTotalCost) / selfFundingTotalCost;
  selfFunding.selfFundingProjectedReturnOnInvestment = returnOnInvestment;

  const investmentCashFlows = convertInvestmentCashFlows(mutableClient);
  const returnCashFlows = convertReturnCashFlows(mutableClient, selfFundingTotalValue);

  // Using the contribution start year as the base year for IRR calculation
  // This ensures each self-funding source has its own timeline
  const baseYear = contributionStartYear ?? new Date().getFullYear();

  const { cagr, internalRateOfReturn, yearsToLastReturn } = analyzeInvestmentReturns(
    investmentCashFlows,
    returnCashFlows,
    discountRate,
    baseYear,
  );

  selfFunding.selfFundingProjectedCompoundAnnualGrowthRate = cagr;
  selfFunding.selfFundingProjectedInternalRateOfReturn = internalRateOfReturn;
  selfFunding.investmentPeriodYears = yearsToLastReturn;
  selfFunding.investmentCashFlows = investmentCashFlows;
  selfFunding.returnCashFlows = returnCashFlows;
  return {
    investments: investmentCashFlows,
    returns: returnCashFlows,
    investmentPeriodYears: yearsToLastReturn,
  };
}

function convertInvestmentCashFlows(mutableClient: Draft<Client>): CashFlow[] {
  const {
    fundingSources: { selfFunding },
  } = mutableClient;
  const { existingAssetsValue, monthlyContributionAmount, contributionMonthCount } = selfFunding;

  const cashFlows: CashFlow[] = [];

  // Add initial investment as year 0
  if (!isNullOrUndefined(existingAssetsValue)) {
    cashFlows.push({
      year: 0,
      amount: existingAssetsValue,
    });
  }

  if (!isNullOrUndefined(monthlyContributionAmount)) {
    // Get full years and remaining months
    const fullYears = Math.floor(contributionMonthCount / 12) ?? 0;
    const remainingMonths = contributionMonthCount % 12;

    // Add full year contributions
    for (let year = 0; year < fullYears; year++) {
      cashFlows.push({
        year: year,
        amount: monthlyContributionAmount * 12,
      });
    }

    // Add partial year contribution if there are remaining months
    if (remainingMonths > 0) {
      cashFlows.push({
        year: fullYears,
        amount: monthlyContributionAmount * remainingMonths,
      });
    }
  }

  return cashFlows;
}

function convertReturnCashFlows(
  mutableClient: Draft<Client>,
  selfFundingTotalValue: number,
): CashFlow[] {
  const {
    allPhaseCosts: { allPhaseCareMonthsNeeded },
    fundingSources: {
      selfFunding: { monthlyIncomeTotal, contributionStartYear, contributionEndYear },
    },
    appliedInferenceSet: { ltcAtYear },
  } = mutableClient;

  const returnYears = Math.ceil(allPhaseCareMonthsNeeded / 12) ?? 1;
  const yearlyAmount = returnYears
    ? (selfFundingTotalValue - (monthlyIncomeTotal ?? 0)) / returnYears
    : 0;

  // Calculate the year offset for when returns start (care start year)
  // Returns should start when care starts, not during the contribution period
  const careStartYear = ltcAtYear ?? contributionEndYear;
  const yearsUntilCareStarts = careStartYear - contributionStartYear;

  return Array.from({ length: returnYears }, (_, index) => ({
    year: yearsUntilCareStarts + index,
    amount: yearlyAmount,
  }));
}
